/**
 * OPVisual wire - the ONE place that knows how an OPButtons intention becomes
 * a message OPVisual accepts, in BOTH of the encodings it accepts.
 *
 * ## Two encodings of one intention (DEC-215)
 *
 * Every builder returns an {@link OpvMsg} carrying the same intention twice:
 *
 * - `frame` - the OPBridge-protocol form, the DEFAULT path. POSTed to the
 *   OPVisual machine's bridge at `/opvisual/trigger` as `{verb, ...fields}`;
 *   OPBridge forwards it unchanged to OPVisual's `/ws/consumer` socket as
 *   `{"event":"opvisual_trigger", verb, ...fields}`. Source of truth is
 *   OPVisual's `app/src/bridge_consumer.rs` (`parse_trigger` /
 *   `parse_control`), read 2026-09-28; every verb and field name below is
 *   one that parser accepts and one its own tests pin.
 * - `osc` - the OSC form, kept as a user-selectable option ("Sends via" in
 *   the Inspector; operator: "keep osc so users can tap into it").
 *
 * Building both in one place is the point: an intention defined twice, in
 * two files, is two intentions that drift apart. The consumer channel's
 * indices are 1-based exactly like the OSC addresses (OPVisual chose that so
 * a rule moved between the two fires the same cell), and it names the layer
 * `row` - resolved in `cellMsg`, the same one place as the column-first
 * inversion below.
 *
 * ## The OSC form
 *
 * Source of truth is OPVisual's own listener, `app/src/clock/osc_in.rs`, read
 * 2026-09-24 - not the prose in its CONTRACT.md alone, because the contract
 * still lists `load_clip` / `zorder`, which the handler does not implement.
 * Every address built here is one that handler matches.
 *
 * ## The inversion this file exists to contain
 *
 * The operator names a cell LAYER FIRST: `L3-5` is layer 3, column 5 - that is
 * what OPVisual's own grid shows. The wire is COLUMN FIRST:
 * `/opvisual/grid/cell <col> <row>`. OPVisual has written that up as a hazard
 * (`.notepads/kind-marks-and-cell-refs-2026-09-18.md`). So everything outside
 * this file speaks `{ col, layer }` by name, and only `cellMsg` orders them.
 *
 * ## Indexing
 *
 * 1-based on the wire and here, both axes. Row 1 = layer 1 = L1 = the BOTTOM
 * layer; OPVisual draws L5 at the top. Out-of-range is REFUSED by OPVisual,
 * never clamped (a clamp turns an off-by-one into a different clip on stage),
 * so the builders here refuse too - they return `null` rather than send a
 * message the far end will drop without telling anyone.
 *
 * ## Numbers
 *
 * OPBridge's `/osc/send` encodes a JSON integer as OSC `i` and anything else
 * as `f` (`opbridge-osc json_to_osc_type`), so opacity `1.0` arrives as int
 * `1`. That is fine: OPVisual's `first_float` accepts Int and `nth_int`
 * accepts Float (read in `osc_in.rs`). No coercion needed on this side.
 */

/** Where OPVisual listens on its own machine. NOT 7000: OPBridge binds
 *  `127.0.0.1:7000` and Windows delivers a loopback datagram to the more
 *  specific bind, so a send to 7000 reaches OPBridge's own listener and
 *  OPVisual never sees it (OPVisual CONTRACT.md s4a, "Which port"). 7011 is
 *  OPVisual's loopback alt port and always works. Loopback relative to the
 *  bridge that sends it, which is why the MACHINE is the routing key. */
export const OPVISUAL_OSC_TARGET = '127.0.0.1:7011';

/**
 * How an OPVisual item reaches OPVisual. Persisted on the item as `sendVia`;
 * absent means `'opbridge'` (DEC-215: OP apps talk through OPBridge in
 * OPBridge protocol).
 *
 * - `'opbridge'` - `POST /opvisual/trigger` on the OPVisual machine's bridge.
 *   The answer says whether an OPVisual was connected to hear it, so a press
 *   that reached nothing is reported rather than lost.
 * - `'osc'` - `POST /osc/send` to {@link OPVISUAL_OSC_TARGET} on that bridge.
 *   Works without OPVisual being linked to OPBridge at all, and reports
 *   nothing back: a press sent while OPVisual is closed is dropped silently.
 */
export type OpvVia = 'opbridge' | 'osc';
export const DEFAULT_OPV_VIA: OpvVia = 'opbridge';

/** `GRID_COLS` in OPVisual's `media/grid.rs`: 10 since 2026-09-29 (was 8;
 *  OPVisual session 48, operator: "i need 2 more columns on the grid").
 *  OPVisual's CONTRACT.md asks controllers to read the size from its `grid`
 *  frame rather than hard-code it. The Grid does (`opvisual/listing.ts`,
 *  through OPBridge's `GET /opvisual/state`, since 2026-10-06); this is the
 *  width before that frame has been read, and for a Trigger, which never
 *  reads it. An OPVisual still on an 8-column build refuses columns 9-10 by
 *  name, never clamps them onto another cell. */
export const OPVISUAL_COLS = 10;
/** `LAYER_COUNT` (= `GRID_ROWS`). Fixed; was 3 before 2026-09-19. */
export const OPVISUAL_LAYERS = 5;

export type OpvQuant = 'bar' | 'now';

export type OpvBlend = 'normal' | 'add' | 'multiply' | 'screen' | 'overlay';
/** In OPVisual's own cycle order, so tapping ours walks the same ladder. */
export const OPV_BLENDS: readonly OpvBlend[] = ['normal', 'add', 'multiply', 'screen', 'overlay'];
export const OPV_BLEND_SHORT: Record<OpvBlend, string> = {
  normal: 'NRM', add: 'ADD', multiply: 'MUL', screen: 'SCR', overlay: 'OVL',
};

/** Speed steps a tap walks through. OPVisual clamps to 0.05..8. */
export const OPV_SPEEDS: readonly number[] = [0.25, 0.5, 1, 2, 4];

export type OpvLookParam = 'brightness' | 'hue' | 'sat' | 'bloom';
export const OPV_LOOK_PARAMS: readonly OpvLookParam[] = ['brightness', 'hue', 'sat', 'bloom'];

/**
 * What an OPVisual Trigger does. Persisted on the item, so every variant is
 * plain data. `layer` is always the OPERATOR's layer number (1 = L1 = bottom).
 */
export type OpVisualAction =
  /** `follow: false` (2026-10-18, OPVisual DEC-148): the cell still counts its
   *  plays and ends, but does not follow on to another cell. Absent = follow. */
  | { verb: 'cell'; col: number; layer: number; follow?: false }
  | { verb: 'column'; col: number; follow?: false }
  | { verb: 'chainToggle' }
  | { verb: 'chainStart' }
  | { verb: 'chainStop' }
  | { verb: 'chainNext' }
  | { verb: 'chainGoto'; col: number }
  | { verb: 'clearLayer'; layer: number }
  | { verb: 'stopAll' }
  | { verb: 'blackout' }
  | { verb: 'operatorScreen' }
  | { verb: 'panic' }
  | { verb: 'reloadLibrary' }
  // Module visualisers (OPVisual CONTRACT s4b, 2026-10-05). `layer` 0 = the
  // newest module visualiser on air (the verb's own default); 1-5 = that lane.
  | { verb: 'vizVariation'; layer: number }
  | { verb: 'vizMacro'; macro: number; value: number; layer: number }
  | { verb: 'palette'; id: string }
  | { verb: 'streamSafe'; mode: 'toggle' | 'on' | 'off' };

export type OpVisualVerb = OpVisualAction['verb'];

/** The verbs whose landing moment the item's `quant` controls. Everything
 *  else is instant by nature (OPVisual takes no quant argument for it). */
export function verbTakesQuant(verb: OpVisualVerb): boolean {
  return verb === 'cell' || verb === 'column' || verb === 'chainGoto' || verb === 'vizVariation';
}

export interface OscMsg {
  address: string;
  args: Array<number | string>;
}

/** The OPBridge-protocol form: the body of `POST /opvisual/trigger`, and -
 *  with `event` added by OPBridge - exactly the frame OPVisual parses. */
export interface OpvFrame {
  verb: string;
  [field: string]: number | string | boolean;
}

/** One intention in both encodings. See the file header. */
export interface OpvMsg {
  osc: OscMsg;
  frame: OpvFrame;
}

function validCol(col: number): boolean {
  return Number.isInteger(col) && col >= 1 && col <= OPVISUAL_COLS;
}
function validLayer(layer: number): boolean {
  return Number.isInteger(layer) && layer >= 1 && layer <= OPVISUAL_LAYERS;
}

/** The operator's name for a cell: layer first, as OPVisual's grid shows it. */
export function cellLabel(col: number, layer: number): string {
  return `L${layer}-${col}`;
}

/** Fire one cell. The ONLY place the layer-first / column-first inversion is
 *  resolved - see the file header. The consumer channel names the layer
 *  `row` (OPVisual's `GRID_ROWS` = `LAYER_COUNT`); same 1-based number. */
export function cellMsg(col: number, layer: number, quant: OpvQuant, follow = true): OpvMsg | null {
  if (!validCol(col) || !validLayer(layer)) return null;
  return {
    osc: { address: '/opvisual/grid/cell', args: [col, layer, quant] },
    // `follow: false` only when asked (OPVisual CONTRACT.md 4b): absent follows,
    // so every frame sent before it is unchanged. The OSC twin carries no
    // follow flag (OPVisual has not defined one).
    frame: { verb: 'grid_cell', col, row: layer, quant, ...(follow ? {} : { follow: false }) },
  };
}

export function columnMsg(col: number, quant: OpvQuant, follow = true): OpvMsg | null {
  if (!validCol(col)) return null;
  return {
    osc: { address: '/opvisual/grid/column', args: [col, quant] },
    frame: { verb: 'grid_column', col, quant, ...(follow ? {} : { follow: false }) },
  };
}

export function chainGotoMsg(col: number, quant: OpvQuant): OpvMsg | null {
  if (!validCol(col)) return null;
  return {
    osc: { address: '/opvisual/chain/goto', args: [col, quant] },
    frame: { verb: 'chain_goto', col, quant },
  };
}

/** `start` carries no column on either channel: OPVisual starts the chain
 *  from where it is, exactly as its own button does. */
export function chainMsg(verb: 'start' | 'stop' | 'toggle' | 'next'): OpvMsg {
  return {
    osc: { address: `/opvisual/chain/${verb}`, args: [] },
    frame: { verb: `chain_${verb}` },
  };
}

/** `clip<N>` is OPVisual's own id for layer N (engine index N-1). */
function layerAddr(layer: number, param: string): string {
  return `/opvisual/layer/clip${layer}/${param}`;
}

export function layerOpacityMsg(layer: number, value: number): OpvMsg | null {
  if (!validLayer(layer)) return null;
  const v = clamp01(value);
  return {
    osc: { address: layerAddr(layer, 'opacity'), args: [v] },
    frame: { verb: 'layer_opacity', layer, value: v },
  };
}

/** `on` is a JSON bool on the consumer channel (OPVisual also takes a number,
 *  as OSC does); the OSC form keeps its `i 1` / `i 0`. */
export function layerEnabledMsg(layer: number, on: boolean): OpvMsg | null {
  if (!validLayer(layer)) return null;
  return {
    osc: { address: layerAddr(layer, 'enabled'), args: [on ? 1 : 0] },
    frame: { verb: 'layer_enabled', layer, on },
  };
}

export function layerBlendMsg(layer: number, mode: OpvBlend): OpvMsg | null {
  if (!validLayer(layer)) return null;
  return {
    osc: { address: layerAddr(layer, 'blend_mode'), args: [mode] },
    frame: { verb: 'layer_blend', layer, mode },
  };
}

export function layerSpeedMsg(layer: number, speed: number): OpvMsg | null {
  if (!validLayer(layer)) return null;
  const v = Math.max(0.05, Math.min(8, speed));
  return {
    osc: { address: layerAddr(layer, 'speed'), args: [v] },
    frame: { verb: 'layer_speed', layer, value: v },
  };
}

export function layerClearMsg(layer: number): OpvMsg | null {
  if (!validLayer(layer)) return null;
  return {
    osc: { address: layerAddr(layer, 'clear'), args: [] },
    frame: { verb: 'layer_clear', layer },
  };
}

/** OPVisual's own STOP ALL (`stop_all()`, the same code path as its local
 *  button): every lane off air, any fire already armed for the next bar
 *  dropped, and the column chain stopped. One clear per layer is NOT the same
 *  thing - `stop_lane` leaves an armed bar fire in place, so a cell could still
 *  land after it. */
export function stopAllMsg(): OpvMsg {
  return {
    osc: { address: '/opvisual/action/stop_all', args: [] },
    frame: { verb: 'stop_all' },
  };
}

export function masterOpacityMsg(value: number): OpvMsg {
  const v = clamp01(value);
  return {
    osc: { address: '/opvisual/global/master_opacity', args: [v] },
    frame: { verb: 'master_opacity', value: v },
  };
}

export function lookMsg(param: OpvLookParam, value: number): OpvMsg {
  const v = clamp01(value);
  return {
    osc: { address: `/opvisual/generative/${param}`, args: [v] },
    frame: { verb: 'look', param, value: v },
  };
}

/** OPVisual's blackout LATCH, the one its own B/OUT button uses. No argument
 *  toggles it: a second press brings the picture back at the master it had
 *  before. Both channels also take a set form (OSC `i 1` / `i 0`, consumer
 *  `on`), which nothing here sends yet. Raising the master also releases the
 *  latch. */
export function blackoutMsg(): OpvMsg {
  return {
    osc: { address: '/opvisual/action/blackout', args: [] },
    frame: { verb: 'blackout' },
  };
}

/** Hide or bring back OPVisual's operator UI. No argument toggles (no
 *  `hidden` field on the consumer channel). It acts at any time, in or out of
 *  GIG, and GIG never hides or shows anything by itself (OPVisual CONTRACT.md,
 *  "independent of GIG since 2026-09-28"). It exists so a pedal can get the
 *  operator back to the UI with nobody at the mouse. */
export function operatorScreenMsg(): OpvMsg {
  return {
    osc: { address: '/opvisual/action/operator_screen', args: [] },
    frame: { verb: 'operator_screen' },
  };
}

export function panicMsg(): OpvMsg {
  return {
    osc: { address: '/opvisual/action/panic', args: [] },
    frame: { verb: 'panic' },
  };
}

export function reloadLibraryMsg(): OpvMsg {
  return {
    osc: { address: '/opvisual/action/reload_library', args: [] },
    frame: { verb: 'reload_library' },
  };
}

/** A new take of a module visualiser (a new seed, crossfaded over a bar).
 *  `layer` 0 = the newest one on air. */
export function vizVariationMsg(layer: number, quant: OpvQuant): OpvMsg | null {
  if (layer === 0) {
    return { osc: { address: '/opvisual/viz/variation', args: [quant] }, frame: { verb: 'viz_variation', quant } };
  }
  if (!validLayer(layer)) return null;
  return {
    osc: { address: `/opvisual/layer/clip${layer}/viz_variation`, args: [quant] },
    frame: { verb: 'viz_variation', layer, quant },
  };
}

/** Set one of a module visualiser's six knobs. Live only: OPVisual saves
 *  nothing, and the next fire of that visualiser brings its stored knobs back. */
export function vizMacroMsg(macro: number, value: number, layer: number): OpvMsg | null {
  if (!Number.isInteger(macro) || macro < 1 || macro > 6) return null;
  const v = clamp01(value);
  if (layer === 0) {
    return { osc: { address: '/opvisual/viz/macro', args: [macro, v] }, frame: { verb: 'viz_macro', macro, value: v } };
  }
  if (!validLayer(layer)) return null;
  return {
    osc: { address: `/opvisual/layer/clip${layer}/viz_macro`, args: [macro, v] },
    frame: { verb: 'viz_macro', macro, value: v, layer },
  };
}

/** The show palette, by id ("neon_noir"), name ("Neon Noir") or 1-based place
 *  in OPVisual's picker. Show-wide; an unknown one is refused by OPVisual. */
export function paletteMsg(id: string): OpvMsg | null {
  const name = id.trim();
  if (!name) return null;
  const n = /^\d+$/.test(name) ? Number(name) : null;
  return {
    osc: { address: '/opvisual/viz/palette', args: [n ?? name] },
    frame: { verb: 'palette', id: n ?? name },
  };
}

/** CAM SHAKE, show-wide (OPVisual CONTRACT.md:268 and :626): shakes every lane
 *  holding a camera or Spout input. Always SETS (never the no-argument toggle),
 *  because the grid's SHAKE is held: down sends on at `amount`, up sends off,
 *  and a toggle would leave it shaking if the release were ever lost. */
export function camShakeMsg(on: boolean, amount?: number): OpvMsg {
  if (!on) return { osc: { address: '/opvisual/cam/shake', args: [0] }, frame: { verb: 'cam_shake', on: false } };
  const a = clamp01(amount ?? 0.5);
  return { osc: { address: '/opvisual/cam/shake', args: [a] }, frame: { verb: 'cam_shake', on: true, amount: a } };
}

/** STREAM SAFE, show-wide: no argument toggles, an argument sets it. */
export function streamSafeMsg(mode: 'toggle' | 'on' | 'off'): OpvMsg {
  if (mode === 'toggle') return { osc: { address: '/opvisual/viz/stream_safe', args: [] }, frame: { verb: 'stream_safe' } };
  const on = mode === 'on';
  return { osc: { address: '/opvisual/viz/stream_safe', args: [on ? 1 : 0] }, frame: { verb: 'stream_safe', on } };
}

/** Every message one Trigger press sends, in order. Empty = the action is not
 *  addressable (an out-of-range index), which the caller reports rather than
 *  sending nothing silently. */
export function actionMsgs(action: OpVisualAction, quant: OpvQuant): OpvMsg[] {
  const one = (m: OpvMsg | null): OpvMsg[] => (m ? [m] : []);
  switch (action.verb) {
    case 'cell':          return one(cellMsg(action.col, action.layer, quant, action.follow !== false));
    case 'column':        return one(columnMsg(action.col, quant, action.follow !== false));
    case 'chainGoto':     return one(chainGotoMsg(action.col, quant));
    case 'chainToggle':   return [chainMsg('toggle')];
    case 'chainStart':    return [chainMsg('start')];
    case 'chainStop':     return [chainMsg('stop')];
    case 'chainNext':     return [chainMsg('next')];
    case 'clearLayer':    return one(layerClearMsg(action.layer));
    case 'stopAll':       return [stopAllMsg()];
    case 'blackout':      return [blackoutMsg()];
    case 'operatorScreen': return [operatorScreenMsg()];
    case 'panic':         return [panicMsg()];
    case 'reloadLibrary': return [reloadLibraryMsg()];
    case 'vizVariation':  return one(vizVariationMsg(action.layer, quant));
    case 'vizMacro':      return one(vizMacroMsg(action.macro, action.value, action.layer));
    case 'palette':       return one(paletteMsg(action.id));
    case 'streamSafe':    return [streamSafeMsg(action.mode)];
  }
}

/** Short face text for a Trigger - what the operator reads on the button. */
export function actionFace(action: OpVisualAction): string {
  switch (action.verb) {
    case 'cell':          return cellLabel(action.col, action.layer);
    case 'column':        return `COLUMN ${action.col}`;
    case 'chainGoto':     return `CHAIN > ${action.col}`;
    case 'chainToggle':   return 'CHAIN';
    case 'chainStart':    return 'CHAIN START';
    case 'chainStop':     return 'CHAIN STOP';
    case 'chainNext':     return 'CHAIN NEXT';
    case 'clearLayer':    return `CLEAR L${action.layer}`;
    case 'stopAll':       return 'STOP ALL';
    case 'blackout':      return 'BLACKOUT';
    case 'operatorScreen': return 'OPV SCREEN';
    case 'panic':       return 'PANIC';
    case 'reloadLibrary': return 'RELOAD';
    case 'vizVariation':  return action.layer ? `NEW TAKE L${action.layer}` : 'NEW TAKE';
    case 'vizMacro':      return `KNOB ${action.macro} ${Math.round(clamp01(action.value) * 100)}%${action.layer ? ` L${action.layer}` : ''}`;
    case 'palette':       return action.id.trim() ? `PALETTE ${action.id.trim().toUpperCase()}` : 'PALETTE';
    case 'streamSafe':    return action.mode === 'toggle' ? 'STREAM SAFE' : `STREAM SAFE ${action.mode.toUpperCase()}`;
  }
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}
